import { gql, platformUpload } from '@/lib/api';
import type { InvitationEmailDelivery } from '@/lib/invitation-email';

export interface TenantEdge extends TenantLifecycle {
  id: string;
  name: string;
  email?: string;
  subdomain?: string;
  nit?: string;
  planTier: string;
  planStatus: string;
  isActive: boolean;
  /**
   * OS-1723 — MRR real (COP) calculado de las líneas de suscripción; 0 fuera de activo/mora.
   * Null sin `platform.billing.read`.
   */
  mrr?: number | null;
  /** OS-1723 — MRR potencial (piloto/prueba). Null sin `platform.billing.read`. */
  potentialMrr?: number | null;
  /** OS-1724 — módulos habilitados (core incluidos). */
  enabledModules?: string[] | null;
  trialEndsAt?: string | null;
  createdAt: string;
  /** OS-1713 — estados a los que se puede llevar el tenant; los decide el API. */
  allowedStatusTransitions: TenantPlanStatus[];
  /** OS-1718 — progreso de onboarding (null en las respuestas de mutaciones). */
  onboarding?: TenantOnboarding | null;
}

/** OS-1718 — pasos del checklist de activación, en el orden del ticket. */
export type TenantOnboardingStepKey =
  | 'COMPANY_DATA'
  | 'ACTIVE_LOCATION'
  | 'BRANDING'
  | 'MODULES'
  | 'ADMIN_LOGIN'
  | 'FIRST_USER'
  | 'FIRST_USAGE';

/** OS-1718 — progreso derivado de los datos del tenant (el API no lo guarda). */
export interface TenantOnboarding {
  tenantId: string;
  steps: { key: TenantOnboardingStepKey; done: boolean }[];
  completedSteps: number;
  totalSteps: number;
  percent: number;
  complete: boolean;
  /** Más de 7 días desde el alta con menos de 50 %, en un estado que opera. */
  stalled: boolean;
}

/** OS-1718 — `INCOMPLETE`: falta algún paso; `STALLED`: estancado. */
export type TenantOnboardingFilter = 'INCOMPLETE' | 'STALLED';

/** OS-1720 — fechas, alerta y acciones del ciclo de vida (las decide el API). */
export interface TenantLifecycle {
  pilotStartsAt?: string | null;
  pilotEndsAt?: string | null;
  convertedAt?: string | null;
  cancelledAt?: string | null;
  expiryAlert?: TenantExpiryAlert | null;
  allowedLifecycleActions: TenantLifecycleOption[];
}

/** OS-1713 / OS-1720 — estados del ciclo de vida (`planStatus`). */
export type TenantPlanStatus = 'pilot' | 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled';

/** OS-1720 — acciones de la máquina de estados del API. */
export type TenantLifecycleAction =
  | 'CONVERT'
  | 'SETTLE_PAST_DUE'
  | 'RESUME'
  | 'REACTIVATE'
  | 'MARK_PAST_DUE'
  | 'SUSPEND'
  | 'CANCEL';

export interface TenantLifecycleOption {
  action: TenantLifecycleAction;
  to: TenantPlanStatus;
  reasonRequired: boolean;
}

/** OS-1720 — alerta de vencimiento del piloto (7/3/1 días), calculada por el API al leer. */
export type TenantExpiryAlertLevel = 'NONE' | 'DAYS_7' | 'DAYS_3' | 'DAYS_1' | 'EXPIRED';

export interface TenantExpiryAlert {
  level: TenantExpiryAlertLevel;
  /** Días de calendario (Colombia) hasta el fin: 0 = vence hoy; negativo = vencido. */
  daysRemaining: number;
  endsAt: string;
}

export interface TenantExpiryAlertItem {
  tenantId: string;
  tenantName: string;
  planStatus: TenantPlanStatus;
  alert: TenantExpiryAlert;
}

/** OS-1713 — orden del listado (cursor estable en el API). */
export type TenantListSort = 'createdAt_desc' | 'createdAt_asc' | 'name_asc' | 'name_desc';

export interface TenantListFilter {
  search?: string;
  planStatus?: string;
  planTier?: string;
  onboarding?: TenantOnboardingFilter;
  sort?: TenantListSort;
  cursor?: string;
  take?: number;
}

export interface TenantStatusImpact {
  activeUsers: number;
  activeOperationalUsers: number;
}

export interface TenantStatusResult extends TenantLifecycle {
  id: string;
  name: string;
  planStatus: string;
  isActive: boolean;
  allowedStatusTransitions: TenantPlanStatus[];
}

export interface TenantsConnection {
  edges: TenantEdge[];
  nextCursor?: string;
  hasNextPage: boolean;
}

/** OS-1719 — sin contraseña: el administrador define la suya con la invitación por email. */
export interface CreateTenantAdminInput {
  email: string;
  cedula: string;
  documentType?: string;
}

/** OS-1719 — invitación del administrador inicial recién emitida (alta o reenvío). */
export interface TenantAdminInvitationResult {
  emailDelivery: InvitationEmailDelivery;
  expiresAt: string | null;
  /** Link de activación de un solo uso: solo si el email no se confirmó. No se vuelve a mostrar. */
  activationUrl: string | null;
}

/** OS-1719 — estado de la invitación del administrador inicial. */
export type TenantAdminInvitationStatus = 'ACTIVATED' | 'PENDING' | 'EXPIRED' | 'NOT_SENT' | 'UNKNOWN';

export interface TenantAdminInvitationState {
  status: TenantAdminInvitationStatus;
  adminUserId: string | null;
  adminEmail: string | null;
  sentAt: string | null;
  expiresAt: string | null;
}

export interface CreateTenantLocationInput {
  name: string;
  address: string;
  city?: string;
  code?: string;
  phone?: string;
}

export interface CreateTenantInput {
  name: string;
  legalName?: string;
  email?: string;
  /** NIT base sin DV (solo dígitos). */
  nit?: string;
  /** OS-1712 — DV DIAN; el API lo valida contra el NIT. */
  verificationDigit?: string;
  subdomain?: string;
  planTier?: string;
  /** ISO 3166-1 alpha-2. Obligatorio: el API no pone CO por defecto. */
  country: string;
  /**
   * OS-1716 — módulos del catálogo a habilitar (el API suma los core y valida dependencias).
   * Omitido: el API habilita todos.
   */
  modules?: string[];
  /** OS-1720 — `YYYY-MM-DD`: con fecha nace como piloto; sin ella, como cliente de pago. */
  pilotEndsAt?: string;
  admin: CreateTenantAdminInput;
  location: CreateTenantLocationInput;
}

export interface CreateTenantResult {
  id: string;
  name: string;
  subdomain?: string;
  planTier: string;
  planStatus: string;
  isActive: boolean;
  pilotEndsAt?: string | null;
  createdAt: string;
  adminUser: { id: string; email: string; roleCode: string };
  location: { id: string; name: string };
  adminInvitation: TenantAdminInvitationResult;
}

export interface TenantBrandingDetail {
  logoUrl?: string | null;
  bannerUrl?: string | null;
  faviconUrl?: string | null;
  colorPrimary: string;
  colorSecondary: string;
  colorSuccess: string;
  colorDanger: string;
  fontFamily: string;
  displayName: string;
}

export interface TenantDetail extends TenantLifecycle {
  id: string;
  name: string;
  legalName?: string;
  email?: string;
  nit?: string;
  /** OS-1713 — DV DIAN (columna aparte del NIT). */
  verificationDigit?: string;
  subdomain?: string;
  planTier: string;
  planStatus: string;
  allowedStatusTransitions: TenantPlanStatus[];
  country: string;
  /** OS-1723 — MRR real y potencial calculados; null sin `platform.billing.read`. */
  mrr?: number | null;
  potentialMrr?: number | null;
  /** OS-1724 — módulos habilitados (core incluidos). */
  enabledModules?: string[];
  /** OS-1720 — motivo de la cancelación vigente. */
  cancelReason?: string | null;
  trialEndsAt?: string | null;
  createdAt: string;
  flexibleAttendanceCheck: boolean;
  /** OS-1519 — interruptor general de certificados laborales automáticos. */
  autoApproveLaborCertificates: boolean;
  branding?: TenantBrandingDetail | null;
}

/** OS-1387 / OS-1519 — update parcial: un campo omitido conserva su valor actual. */
export interface TenantConfigInput {
  flexibleAttendanceCheck?: boolean;
  autoApproveLaborCertificates?: boolean;
}

export interface TenantConfigResult {
  id: string;
  flexibleAttendanceCheck: boolean;
  autoApproveLaborCertificates: boolean;
}

/** Valor del enum GraphQL `CertificateRequestType`; string para no romper con tipos nuevos. */
export type CertificateRequestType = string;

/** OS-1519 — configuración efectiva de un tipo de certificado (sin fila = requiere aprobación manual). */
export interface CertificateTypeConfig {
  type: CertificateRequestType;
  requiresManualApproval: boolean;
}

/** OS-1720 — campos del ciclo de vida que la consola muestra en listado, ficha y resultados. */
const LIFECYCLE_FIELDS = `
      pilotStartsAt pilotEndsAt convertedAt cancelledAt
      expiryAlert { level daysRemaining endsAt }
      allowedLifecycleActions { action to reasonRequired }
`;

const GET_TENANT = `
  query PlatformTenant($id: ID!) {
    platformTenant(id: $id) {
      id name legalName email nit verificationDigit subdomain planTier planStatus country
      allowedStatusTransitions cancelReason trialEndsAt createdAt mrr potentialMrr enabledModules
      ${LIFECYCLE_FIELDS}
      flexibleAttendanceCheck
      autoApproveLaborCertificates
      branding {
        logoUrl bannerUrl faviconUrl
        colorPrimary colorSecondary colorSuccess colorDanger
        fontFamily displayName
      }
    }
  }
`;

const ONBOARDING_FIELDS =
  'tenantId completedSteps totalSteps percent complete stalled steps { key done }';

const LIST_TENANTS = `
  query PlatformTenants($filter: PlatformTenantsFilterInput) {
    platformTenants(filter: $filter) {
      edges {
        id name email subdomain nit planTier planStatus isActive mrr potentialMrr enabledModules trialEndsAt createdAt
        allowedStatusTransitions
        ${LIFECYCLE_FIELDS}
        onboarding { ${ONBOARDING_FIELDS} }
      }
      nextCursor
      hasNextPage
    }
  }
`;

const CREATE_TENANT = `
  mutation CreatePlatformTenant($input: CreatePlatformTenantInput!) {
    createPlatformTenant(input: $input) {
      id
      name
      subdomain
      planTier
      planStatus
      isActive
      pilotEndsAt
      createdAt
      adminUser { id email roleCode }
      location { id name }
      adminInvitation { emailDelivery { status willRetry } expiresAt activationUrl }
    }
  }
`;

/**
 * OS-1713 — edición parcial: un campo omitido conserva su valor; `null` borra los opcionales
 * (razón social, email, NIT con su DV, subdominio).
 */
export interface UpdateTenantInput {
  name?: string;
  legalName?: string | null;
  email?: string | null;
  nit?: string | null;
  verificationDigit?: string | null;
  subdomain?: string | null;
  planTier?: string;
  country?: string;
}

/** OS-1712 — motivo por el que un subdominio no se puede usar. */
export type SubdomainUnavailableReason = 'INVALID_FORMAT' | 'RESERVED' | 'TAKEN';

export interface SubdomainAvailability {
  subdomain: string;
  available: boolean;
  reason?: SubdomainUnavailableReason | null;
}

const SUBDOMAIN_AVAILABLE = `
  query PlatformSubdomainAvailable($subdomain: String!, $tenantId: ID) {
    platformSubdomainAvailable(subdomain: $subdomain, tenantId: $tenantId) {
      subdomain
      available
      reason
    }
  }
`;

/** OS-1725 C1b — `tenantId` al editar: el propio tenant (su id o subdominio) no cuenta como ocupante. */
export async function checkSubdomainAvailability(
  subdomain: string,
  tenantId?: string,
): Promise<SubdomainAvailability> {
  const data = await gql<{ platformSubdomainAvailable: SubdomainAvailability }>(
    SUBDOMAIN_AVAILABLE,
    { subdomain, tenantId: tenantId ?? null },
  );
  return data.platformSubdomainAvailable;
}

const UPDATE_TENANT = `
  mutation UpdatePlatformTenant($id: ID!, $input: UpdatePlatformTenantInput!) {
    updatePlatformTenant(id: $id, input: $input) {
      id name email subdomain nit planTier planStatus isActive mrr potentialMrr enabledModules trialEndsAt createdAt
      allowedStatusTransitions
      ${LIFECYCLE_FIELDS}
    }
  }
`;

const UPDATE_STATUS = `
  mutation UpdatePlatformTenantStatus($id: ID!, $planStatus: String!, $reason: String) {
    updatePlatformTenantStatus(id: $id, planStatus: $planStatus, reason: $reason) {
      id name planStatus isActive allowedStatusTransitions
      ${LIFECYCLE_FIELDS}
    }
  }
`;

const UPDATE_PILOT_END = `
  mutation UpdatePlatformTenantPilotEnd($id: ID!, $pilotEndsAt: String!, $reason: String!) {
    updatePlatformTenantPilotEnd(id: $id, pilotEndsAt: $pilotEndsAt, reason: $reason) {
      id name planStatus isActive allowedStatusTransitions
      ${LIFECYCLE_FIELDS}
    }
  }
`;

const EXPIRY_ALERTS = `
  query PlatformTenantExpiryAlerts($take: Int) {
    platformTenantExpiryAlerts(take: $take) {
      tenantId tenantName planStatus
      alert { level daysRemaining endsAt }
    }
  }
`;

const UPDATE_TENANT_CONFIG = `
  mutation UpdateTenantConfig($tenantId: ID!, $input: UpdateTenantConfigInput!) {
    updateTenantConfig(tenantId: $tenantId, input: $input) {
      id
      flexibleAttendanceCheck
      autoApproveLaborCertificates
    }
  }
`;

const LIST_TENANT_CERTIFICATE_TYPE_CONFIGS = `
  query PlatformTenantCertificateTypeConfigs($tenantId: ID!) {
    platformTenantCertificateTypeConfigs(tenantId: $tenantId) {
      type
      requiresManualApproval
    }
  }
`;

const UPDATE_TENANT_CERTIFICATE_TYPE_CONFIG = `
  mutation UpdateTenantCertificateTypeConfig(
    $tenantId: ID!
    $type: CertificateRequestType!
    $requiresManualApproval: Boolean!
  ) {
    updateTenantCertificateTypeConfig(
      tenantId: $tenantId
      type: $type
      requiresManualApproval: $requiresManualApproval
    ) {
      type
      requiresManualApproval
    }
  }
`;

const UPDATE_PLATFORM_TENANT_BRANDING = `
  mutation UpdatePlatformTenantBranding($tenantId: ID!, $input: UpdatePlatformTenantBrandingInput!) {
    updatePlatformTenantBranding(tenantId: $tenantId, input: $input) {
      logoUrl bannerUrl faviconUrl
      colorPrimary colorSecondary colorSuccess colorDanger
      fontFamily displayName
    }
  }
`;

const STATUS_IMPACT = `
  query PlatformTenantStatusImpact($id: ID!) {
    platformTenantStatusImpact(id: $id) { activeUsers activeOperationalUsers }
  }
`;

export async function getTenantStatusImpact(id: string): Promise<TenantStatusImpact> {
  const data = await gql<{ platformTenantStatusImpact: TenantStatusImpact }>(STATUS_IMPACT, { id });
  return data.platformTenantStatusImpact;
}

export async function listTenants(filter?: TenantListFilter): Promise<TenantsConnection> {
  const data = await gql<{ platformTenants: TenantsConnection }>(LIST_TENANTS, { filter });
  return data.platformTenants;
}

export async function getTenant(id: string): Promise<TenantDetail> {
  const data = await gql<{ platformTenant: TenantDetail }>(GET_TENANT, { id });
  return data.platformTenant;
}

export async function createTenant(input: CreateTenantInput): Promise<CreateTenantResult> {
  const data = await gql<{ createPlatformTenant: CreateTenantResult }>(CREATE_TENANT, { input });
  return data.createPlatformTenant;
}

export async function updateTenant(id: string, input: UpdateTenantInput): Promise<TenantEdge> {
  const data = await gql<{ updatePlatformTenant: TenantEdge }>(UPDATE_TENANT, { id, input });
  return data.updatePlatformTenant;
}

/**
 * OS-1713 / OS-1720 — transición del ciclo de vida. El motivo lo exige el API según la acción
 * (`reasonRequired`); queda en la auditoría.
 */
export async function updateTenantStatus(
  id: string,
  planStatus: TenantPlanStatus,
  reason?: string,
): Promise<TenantStatusResult> {
  const data = await gql<{ updatePlatformTenantStatus: TenantStatusResult }>(UPDATE_STATUS, {
    id,
    planStatus,
    reason: reason || null,
  });
  return data.updatePlatformTenantStatus;
}

export async function updateTenantConfig(
  tenantId: string,
  input: TenantConfigInput,
): Promise<TenantConfigResult> {
  const data = await gql<{ updateTenantConfig: TenantConfigResult }>(UPDATE_TENANT_CONFIG, {
    tenantId,
    input,
  });
  return data.updateTenantConfig;
}

export async function listTenantCertificateTypeConfigs(
  tenantId: string,
): Promise<CertificateTypeConfig[]> {
  const data = await gql<{ platformTenantCertificateTypeConfigs: CertificateTypeConfig[] }>(
    LIST_TENANT_CERTIFICATE_TYPE_CONFIGS,
    { tenantId },
  );
  return data.platformTenantCertificateTypeConfigs;
}

export async function updateTenantCertificateTypeConfig(
  tenantId: string,
  type: CertificateRequestType,
  requiresManualApproval: boolean,
): Promise<CertificateTypeConfig> {
  const data = await gql<{ updateTenantCertificateTypeConfig: CertificateTypeConfig }>(
    UPDATE_TENANT_CERTIFICATE_TYPE_CONFIG,
    { tenantId, type, requiresManualApproval },
  );
  return data.updateTenantCertificateTypeConfig;
}

export type UpdateBrandingInput = {
  colorPrimary?: string;
  colorSecondary?: string;
  colorSuccess?: string;
  colorDanger?: string;
  fontFamily?: string;
  displayName?: string;
  logoUrl?: string | null;
  bannerUrl?: string | null;
};

export async function updatePlatformTenantBranding(
  tenantId: string,
  input: UpdateBrandingInput,
): Promise<TenantBrandingDetail> {
  const data = await gql<{ updatePlatformTenantBranding: TenantBrandingDetail }>(
    UPDATE_PLATFORM_TENANT_BRANDING,
    { tenantId, input },
  );
  return data.updatePlatformTenantBranding;
}

export async function uploadPlatformTenantLogo(
  tenantId: string,
  file: File,
): Promise<{ logoUrl: string; branding: TenantBrandingDetail }> {
  const form = new FormData();
  form.append('file', file);
  return platformUpload(`/platform/tenants/${tenantId}/logo`, form);
}

export async function uploadPlatformTenantBanner(
  tenantId: string,
  file: File,
): Promise<{ bannerUrl: string; branding: TenantBrandingDetail }> {
  const form = new FormData();
  form.append('file', file);
  return platformUpload(`/platform/tenants/${tenantId}/banner`, form);
}

export const HEX_COLOR_RE = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR_RE.test(value);
}

/** OS-1720 — nueva fecha de fin (`YYYY-MM-DD`) de un piloto vigente; el motivo es obligatorio. */
export async function updateTenantPilotEnd(
  id: string,
  pilotEndsAt: string,
  reason: string,
): Promise<TenantStatusResult> {
  const data = await gql<{ updatePlatformTenantPilotEnd: TenantStatusResult }>(UPDATE_PILOT_END, {
    id,
    pilotEndsAt,
    reason,
  });
  return data.updatePlatformTenantPilotEnd;
}

/** OS-1720 — pilotos a 7 días o menos de vencer, o vencidos (más urgente primero). */
export async function listTenantExpiryAlerts(take = 20): Promise<TenantExpiryAlertItem[]> {
  const data = await gql<{ platformTenantExpiryAlerts: TenantExpiryAlertItem[] }>(EXPIRY_ALERTS, {
    take,
  });
  return data.platformTenantExpiryAlerts;
}

const ADMIN_INVITATION_STATE_FIELDS = 'status adminUserId adminEmail sentAt expiresAt';
const ADMIN_INVITATION_RESULT_FIELDS =
  'emailDelivery { status willRetry } expiresAt activationUrl';

/** OS-1719 — estado de la invitación del administrador inicial (sin token ni link). */
export async function getTenantAdminInvitation(tenantId: string): Promise<TenantAdminInvitationState> {
  const data = await gql<{ platformTenantAdminInvitation: TenantAdminInvitationState }>(
    `query TenantAdminInvitation($tenantId: ID!) {
      platformTenantAdminInvitation(tenantId: $tenantId) { ${ADMIN_INVITATION_STATE_FIELDS} }
    }`,
    { tenantId },
  );
  return data.platformTenantAdminInvitation;
}

/** OS-1719 — reenvía la invitación; el link anterior deja de servir. */
export async function resendTenantAdminInvitation(
  tenantId: string,
): Promise<TenantAdminInvitationResult> {
  const data = await gql<{ resendPlatformTenantAdminInvitation: TenantAdminInvitationResult }>(
    `mutation ResendTenantAdminInvitation($tenantId: ID!) {
      resendPlatformTenantAdminInvitation(tenantId: $tenantId) { ${ADMIN_INVITATION_RESULT_FIELDS} }
    }`,
    { tenantId },
  );
  return data.resendPlatformTenantAdminInvitation;
}

/** OS-1719 — revoca el link vigente (motivo obligatorio, queda en la auditoría). */
export async function revokeTenantAdminInvitation(
  tenantId: string,
  reason: string,
): Promise<TenantAdminInvitationState> {
  const data = await gql<{ revokePlatformTenantAdminInvitation: TenantAdminInvitationState }>(
    `mutation RevokeTenantAdminInvitation($tenantId: ID!, $reason: String!) {
      revokePlatformTenantAdminInvitation(tenantId: $tenantId, reason: $reason) { ${ADMIN_INVITATION_STATE_FIELDS} }
    }`,
    { tenantId, reason },
  );
  return data.revokePlatformTenantAdminInvitation;
}

/** OS-1718 — checklist de activación del tenant para la ficha (calculado al leer). */
export async function getTenantOnboarding(tenantId: string): Promise<TenantOnboarding> {
  const data = await gql<{ platformTenantOnboarding: TenantOnboarding }>(
    `query TenantOnboarding($tenantId: ID!) {
      platformTenantOnboarding(tenantId: $tenantId) { ${ONBOARDING_FIELDS} }
    }`,
    { tenantId },
  );
  return data.platformTenantOnboarding;
}

/** OS-1722 — requisitos para convertir a pago. */
export type BillingRequirement = 'legalName' | 'nit' | 'billingEmail';

/**
 * OS-1722 — perfil de facturación: la identidad legal viene de `Tenant` (la misma de Datos
 * generales) y el resto son metadatos de cobro. Sin datos de tarjeta.
 */
export interface TenantBillingProfile {
  tenantId: string;
  legalName: string | null;
  nit: string | null;
  verificationDigit: string | null;
  taxRegime: string | null;
  address: string | null;
  city: string | null;
  department: string | null;
  billingEmail: string | null;
  billingContactName: string | null;
  billingContactPhone: string | null;
  billingCycle: string | null;
  paymentMethodType: string | null;
  notes: string | null;
  exists: boolean;
  complete: boolean;
  missing: BillingRequirement[];
  planStatus: string;
  convertedAt: string | null;
  updatedAt: string | null;
}

export interface UpdateTenantBillingProfileInput {
  legalName: string;
  nit: string;
  verificationDigit: string;
  taxRegime: string | null;
  address: string | null;
  city: string | null;
  department: string | null;
  billingEmail: string;
  billingContactName: string | null;
  billingContactPhone: string | null;
  billingCycle: string;
  paymentMethodType: string | null;
  notes: string | null;
}

const BILLING_PROFILE_FIELDS = `tenantId legalName nit verificationDigit taxRegime address city department
  billingEmail billingContactName billingContactPhone billingCycle paymentMethodType notes
  exists complete missing planStatus convertedAt updatedAt`;

/** OS-1722 — `platform.billing.read`. */
export async function getTenantBillingProfile(tenantId: string): Promise<TenantBillingProfile> {
  const data = await gql<{ platformTenantBillingProfile: TenantBillingProfile }>(
    `query TenantBillingProfile($tenantId: ID!) {
      platformTenantBillingProfile(tenantId: $tenantId) { ${BILLING_PROFILE_FIELDS} }
    }`,
    { tenantId },
  );
  return data.platformTenantBillingProfile;
}

/** OS-1722 — `platform.billing.write`. NIT y DV se validan otra vez en el API (algoritmo DIAN). */
export async function updateTenantBillingProfile(
  tenantId: string,
  input: UpdateTenantBillingProfileInput,
): Promise<TenantBillingProfile> {
  const data = await gql<{ updatePlatformTenantBillingProfile: TenantBillingProfile }>(
    `mutation UpdateTenantBillingProfile($tenantId: ID!, $input: UpdatePlatformTenantBillingProfileInput!) {
      updatePlatformTenantBillingProfile(tenantId: $tenantId, input: $input) { ${BILLING_PROFILE_FIELDS} }
    }`,
    { tenantId, input },
  );
  return data.updatePlatformTenantBillingProfile;
}
